import React from "react";
import { CCSpinner } from "@pymblecitizencoders/cc-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReviewIssue } from "../../types/event";
import { ORDER, SEVERITY, severityOf } from "./eventReviewSeverity";
import { Tooltip } from "../common/Tooltip";
import "./eventManager.css";

/**
 * The issues card's hover text. Says it is an AI's analysis, so a suggestion
 * reads as advice to weigh, and says so differently on the rare check where
 * the assistant was unavailable and only the fixed checks ran.
 */
const reviewHelp = (aiUsed: boolean): string =>
	aiUsed
		? "An AI analysis of this event. It looks for clashes with other events, assessments and sport, and for details not filled in yet. Check its suggestions before acting on them."
		: "The standard checks of this event, as the AI assistant was unavailable. They look for clashes with other events, assessments and sport, and for details not filled in yet.";

interface EventReviewResultProps {
	readOnly: boolean;
	/** Issues in the current event check; `null` when there is no check. */
	issues: ReviewIssue[] | null;
	/** A check is in progress; the card's place says so until it lands. */
	running: boolean;
	/** The check was run on an earlier version than the one saved now. */
	stale: boolean;
	/** False when the assistant was unavailable and these are the fixed checks. */
	aiUsed: boolean;
	/** Opens the event check's modal — see EventReviewModal. */
	onOpen: () => void;
}

/**
 * The event check's result: "Checking for issues..." while it runs, then an
 * "N issues found" card tinted by the most serious one. Only when there is
 * something to act on; a clean check is already a toast. It sits at the top
 * right of the editor, where it can be seen without scrolling the sidebar.
 */
export const EventReviewResult: React.FC<EventReviewResultProps> = ({
	readOnly,
	issues,
	running,
	stale,
	aiUsed,
	onOpen,
}) => {
	return (
		<>
			{!readOnly && running && (
				/* cc-ui's inline spinner drops its label, so the words sit
				   beside it and the span announces them; the spinner is
				   `decorative` so the wait is not read out twice. */
				<span className="em-side-review-status" role="status">
					<CCSpinner size="inline" decorative />
					Checking for issues...
				</span>
			)}
			{!readOnly &&
				!running &&
				issues &&
				issues.length > 0 && (
					/* A card, tinted by the most serious issue in it, so the
					   result stands out from the form around it and says how
					   much it matters before anything is read.

					   The "?" SAYS WHAT IT IS: an AI's reading of the event, to be
					   weighed rather than obeyed. Same pattern as the filter
					   rail's toggles (see ToggleRow in EventFilters): the button
					   names itself with the whole explanation, and `title` is the
					   touch fallback for a hover-only tooltip. */
					<div
						className={`em-side-review em-side-review--${
							ORDER.find((sev) =>
								issues.some((i) => severityOf(i.Severity) === sev),
							) ?? "low"
						}`}
					>
						<button
							type="button"
							className="em-side-review-link"
							onClick={onOpen}
						>
							{issues.length}{" "}
							{issues.length === 1 ? "issue" : "issues"} found
						</button>
						{/* The count by severity, on the link's line, with the
						    modal's own icons, so "3 issues" says whether any of
						    them matter before anyone opens it. Icon and number
						    only: the words do not fit beside the link in a 260px
						    column, so each level's name is its tooltip and what a
						    screen reader hears. Only the severities present, as
						    in the modal's key. */}
						<ul className="em-side-review__breakdown">
							{ORDER.map((sev) => ({
								sev,
								n: issues.filter(
									(i) => severityOf(i.Severity) === sev,
								).length,
							}))
								.filter(({ n }) => n > 0)
								.map(({ sev, n }) => (
									<li key={sev} title={SEVERITY[sev].label}>
										<FontAwesomeIcon
											icon={SEVERITY[sev].icon}
											className={`em-review__icon em-review__icon--${sev}`}
											aria-hidden="true"
										/>
										<span className="em-review__legend-count">{n}</span>
										<span className="em-sr-only">
											{SEVERITY[sev].label}
										</span>
									</li>
								))}
						</ul>
						<Tooltip
							label={reviewHelp(aiUsed)}
							interactive
							className="em-side-review__help-tip"
						>
							<button
								type="button"
								className="em-filter-help em-side-review__help"
								aria-label={`About this check: ${reviewHelp(aiUsed)}`}
								title={reviewHelp(aiUsed)}
							>
								<span aria-hidden="true">?</span>
							</button>
						</Tooltip>
						{/* Kept rather than hidden: an old list still says what to
						    look at, as long as it says it is old. */}
						{stale && (
							<p className="em-side-review__stale">
								From before the latest changes
							</p>
						)}
					</div>
				)}
		</>
	);
};
